/**
 * Unified Navigation Module
 * Handles sidebar collapse/expand, mobile menu, and responsive behavior
 */
class Navigation {
    constructor() {
        // Cache DOM elements on initialization
        this.sidebar = document.querySelector('[data-sidebar]') || document.querySelector('.main-sidebar');
        this.mobileSidebar = document.querySelector('[data-mobile-sidebar]');
        this.toggleButton = null; // Will be cached on first use
        this.toggleIcon = null; // Will be cached on first use
        this.mainContent = null; // Will be cached on first use
        this.breakpointDesktop = 1024; // Desktop breakpoint
        this.breakpointMobile = 640;
        this.state = this.loadState();
        this.dropdowns = new Map();
        this.accountMenuOpen = this.state.accountMenuOpen || false;
        this.moderationMenuOpen = this.state.moderationMenuOpen || false;
        
        if (this.sidebar || this.mobileSidebar) {
            this.init();
        }
    }
    
    init() {
        this.bindToggle();
        this.bindAccountToggle();
        this.bindModerationToggle();
        this.bindDropdowns();
        this.bindMobileMenu();
        this.bindKeyboard();
        this.bindLayoutToggle();
        this.handleResize();

        // Apply initial sidebar state
        this.applySidebarState();
    }
    
    applySidebarState() {
        const mainContent = document.querySelector('.sidebar-content');
        const shouldCollapse = this.state.collapsed;
        
        if (shouldCollapse) {
            if (this.sidebar) {
                this.sidebar.classList.add('sidebar-collapsed');
                document.body.classList.add('sidebar-collapsed');
            }
            if (mainContent) {
                mainContent.setAttribute('data-sidebar-collapsed', 'true');
            }
        } else {
            if (this.sidebar) {
                this.sidebar.classList.remove('sidebar-collapsed');
                document.body.classList.remove('sidebar-collapsed');
            }
            if (mainContent) {
                mainContent.setAttribute('data-sidebar-collapsed', 'false');
            }
        }
    }
    
    // Check if current page should start with collapsed sidebar
    isPageWithForcedSidebar() {
        const path = window.location.pathname;
        // Keep in sync with PHP Controller::PAGES_WITH_FORCED_COLLAPSED_SIDEBAR
        const forcedCollapsePages = ['/app/messages'];
        return forcedCollapsePages.some(page => path === page || path.startsWith(page + '?'));
    }
    
    loadState() {
        // Load from cookies
        const cookies = document.cookie.split(';').map(c => c.trim());
        const sidebarState = cookies.find(c => c.startsWith('sidebar_state='));
        const navLayout = cookies.find(c => c.startsWith('nav_layout='));
        const accountMenuState = cookies.find(c => c.startsWith('account_menu_state='));
        const moderationMenuState = cookies.find(c => c.startsWith('moderation_menu_state='));

        // Override collapsed state for specific pages
        const collapsed = this.isPageWithForcedSidebar()
            ? true
            : (sidebarState?.includes('collapsed') || false);

        return {
            collapsed: collapsed,
            layout: navLayout?.split('=')[1] || 'sidebar',
            accountMenuOpen: accountMenuState?.includes('expanded') || false,
            moderationMenuOpen: moderationMenuState?.includes('expanded') || false
        };
    }
    
    saveState(key, value) {
        const maxAge = 31536000; // 1 year
        document.cookie = `${key}=${value}; path=/; max-age=${maxAge}; SameSite=Lax`;
    }
    
    bindToggle() {
        try {
            // Cache toggle button reference
            this.toggleButton = document.querySelector('[data-sidebar-toggle]');
            
            if (this.toggleButton && !this.toggleButton.hasAttribute('data-bound')) {
                this.toggleButton.setAttribute('data-bound', 'true');
                this.toggleButton.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.toggleSidebar();
                });
            }
        } catch (error) {
            console.error('Error binding toggle button:', error);
        }
    }
    
    bindAccountToggle() {
        try {
            const accountToggle = document.querySelector('[data-account-toggle]');
            if (!accountToggle || accountToggle.hasAttribute('data-bound')) return;

            const userMenuPopover = document.getElementById('user-menu-popover');
            const accountChevron = document.querySelector('[data-account-chevron]');

            // Register user menu popover with dropdown system
            if (userMenuPopover) {
                this.dropdowns.set('user-menu-popover', {
                    button: accountToggle,
                    dropdown: userMenuPopover
                });

                // Prevent scroll from bubbling to page when scrolling inside menu
                userMenuPopover.addEventListener('wheel', (e) => {
                    const { scrollTop, scrollHeight, clientHeight } = userMenuPopover;
                    const isScrollable = scrollHeight > clientHeight;

                    if (!isScrollable) {
                        e.preventDefault();
                        return;
                    }

                    const atTop = scrollTop === 0;
                    const atBottom = scrollTop + clientHeight >= scrollHeight;

                    // Prevent scroll if at boundaries
                    if ((e.deltaY < 0 && atTop) || (e.deltaY > 0 && atBottom)) {
                        e.preventDefault();
                    }
                }, { passive: false });
            }

            accountToggle.setAttribute('data-bound', 'true');

            accountToggle.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();

                // Close moderation menu if open (mutually exclusive)
                if (this.moderationMenuOpen) {
                    const modMenuExpanded = document.querySelector('[data-moderation-menu-expanded]');
                    const modToggle = document.querySelector('[data-moderation-toggle]');
                    const modChevron = document.querySelector('[data-moderation-chevron]');
                    if (modMenuExpanded) {
                        this.collapseModerationMenu(modMenuExpanded, modToggle, modChevron);
                        this.moderationMenuOpen = false;
                        this.saveState('moderation_menu_state', 'collapsed');
                    }
                }

                // Always use popover menu
                this.toggleDropdown('user-menu-popover');
            });
        } catch (error) {
            console.error('Error binding account toggle:', error);
        }
    }
    
    
    expandAccountMenu(menu, toggle, chevron) {
        menu.classList.remove('grid-rows-[0fr]');
        menu.classList.add('grid-rows-[1fr]');
        toggle.setAttribute('aria-expanded', 'true');
        
        // Rotate chevron
        if (chevron) {
            chevron.classList.add('rotate-180');
        }
        
        // Animate menu items with stagger - query only when needed
        const items = menu.querySelectorAll('.account-menu-item');
        items.forEach((item, index) => {
            setTimeout(() => {
                item.classList.remove('opacity-0', 'translate-x-2');
                item.classList.add('opacity-100', 'translate-x-0');
            }, index * 50);
        });
    }
    
    collapseAccountMenu(menu, toggle, chevron) {
        menu.classList.remove('grid-rows-[1fr]');
        menu.classList.add('grid-rows-[0fr]');
        toggle.setAttribute('aria-expanded', 'false');

        // Rotate chevron back
        if (chevron) {
            chevron.classList.remove('rotate-180');
        }

        // Reset menu items - query only when needed
        const items = menu.querySelectorAll('.account-menu-item');
        items.forEach(item => {
            item.classList.remove('opacity-100', 'translate-x-0');
            item.classList.add('opacity-0', 'translate-x-2');
        });
    }

    bindModerationToggle() {
        try {
            const moderationToggle = document.querySelector('[data-moderation-toggle]');
            if (!moderationToggle || moderationToggle.hasAttribute('data-bound')) return;

            const moderationMenuExpanded = document.querySelector('[data-moderation-menu-expanded]');
            const moderationDropdown = document.getElementById('moderation-dropdown');
            const moderationChevron = document.querySelector('[data-moderation-chevron]');

            // Register moderation dropdown with existing dropdown system
            if (moderationDropdown) {
                this.dropdowns.set('moderation-dropdown', {
                    button: moderationToggle,
                    dropdown: moderationDropdown
                });
            }

            moderationToggle.setAttribute('data-bound', 'true');

            moderationToggle.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();

                const isCollapsed = this.sidebar && this.sidebar.classList.contains('sidebar-collapsed');
                const isResponsiveCollapsed = window.innerWidth >= 640 && window.innerWidth < 1024;

                // Close account menu if open (mutually exclusive)
                if (this.accountMenuOpen) {
                    const accMenuExpanded = document.querySelector('[data-account-menu-expanded]');
                    const accToggle = document.querySelector('[data-account-toggle]');
                    const accChevron = document.querySelector('[data-account-chevron]');
                    if (accMenuExpanded) {
                        this.collapseAccountMenu(accMenuExpanded, accToggle, accChevron);
                        this.accountMenuOpen = false;
                        this.saveState('account_menu_state', 'collapsed');
                    }
                }

                if (isCollapsed || isResponsiveCollapsed) {
                    // In collapsed mode (manual or responsive), use dropdown system
                    this.toggleDropdown('moderation-dropdown');
                } else {
                    // In expanded mode, show inline menu
                    if (moderationMenuExpanded) {
                        this.moderationMenuOpen = !this.moderationMenuOpen;

                        if (this.moderationMenuOpen) {
                            this.expandModerationMenu(moderationMenuExpanded, moderationToggle, moderationChevron);
                        } else {
                            this.collapseModerationMenu(moderationMenuExpanded, moderationToggle, moderationChevron);
                        }

                        // Save state to cookie
                        this.saveState('moderation_menu_state', this.moderationMenuOpen ? 'expanded' : 'collapsed');
                    }
                }
            });
        } catch (error) {
            console.error('Error binding moderation toggle:', error);
        }
    }

    expandModerationMenu(menu, toggle, chevron) {
        menu.classList.remove('grid-rows-[0fr]');
        menu.classList.add('grid-rows-[1fr]');
        toggle.setAttribute('aria-expanded', 'true');

        // Rotate chevron
        if (chevron) {
            chevron.classList.add('rotate-180');
        }

        // Animate menu items with stagger
        const items = menu.querySelectorAll('.moderation-menu-item');
        items.forEach((item, index) => {
            setTimeout(() => {
                item.classList.remove('opacity-0', 'translate-x-2');
                item.classList.add('opacity-100', 'translate-x-0');
            }, index * 50);
        });
    }

    collapseModerationMenu(menu, toggle, chevron) {
        menu.classList.remove('grid-rows-[1fr]');
        menu.classList.add('grid-rows-[0fr]');
        toggle.setAttribute('aria-expanded', 'false');

        // Rotate chevron back
        if (chevron) {
            chevron.classList.remove('rotate-180');
        }

        // Reset menu items
        const items = menu.querySelectorAll('.moderation-menu-item');
        items.forEach(item => {
            item.classList.remove('opacity-100', 'translate-x-0');
            item.classList.add('opacity-0', 'translate-x-2');
        });
    }

    toggleSidebar() {
        try {
            if (!this.sidebar) return;
            
            const isCurrentlyCollapsed = this.sidebar.classList.contains('sidebar-collapsed');
            
            if (isCurrentlyCollapsed) {
                // Expand the sidebar
                this.sidebar.classList.remove('sidebar-collapsed');
                document.body.classList.remove('sidebar-collapsed');
            } else {
                // Collapse the sidebar
                this.sidebar.classList.add('sidebar-collapsed');
                document.body.classList.add('sidebar-collapsed');
            }
            
            // Update main content margins using data attribute
            this.mainContent = this.mainContent || document.querySelector('.sidebar-content');
            if (this.mainContent) {
                this.mainContent.setAttribute('data-sidebar-collapsed', isCurrentlyCollapsed ? 'false' : 'true');
            }
            
            // Update toggle button icon (cache reference)
            this.toggleIcon = this.toggleIcon || document.querySelector('[data-sidebar-toggle] i');
            if (this.toggleIcon) {
                if (isCurrentlyCollapsed) {
                    // Now expanded - show left chevron to collapse
                    this.toggleIcon.classList.remove('fa-chevron-right');
                    this.toggleIcon.classList.add('fa-chevron-left');
                } else {
                    // Now collapsed - show right chevron to expand
                    this.toggleIcon.classList.remove('fa-chevron-left');
                    this.toggleIcon.classList.add('fa-chevron-right');
                }
            }
            
            // Save state
            this.saveState('sidebar_state', isCurrentlyCollapsed ? 'expanded' : 'collapsed');
            
            // Trigger resize event for any components that need to adjust
            window.dispatchEvent(new Event('resize'));
        } catch (error) {
            console.error('Error toggling sidebar:', error);
        }
    }
    
    bindDropdowns() {
        try {
            // Handle sidebar dropdowns (exclude account/moderation toggles as they're handled separately)
            document.querySelectorAll('[data-dropdown-toggle]:not([data-account-toggle]):not([data-moderation-toggle])').forEach(button => {
                const targetId = button.getAttribute('data-dropdown-toggle');
                const dropdown = document.getElementById(targetId);
                
                if (dropdown) {
                    this.dropdowns.set(targetId, { button, dropdown });
                    
                    button.addEventListener('click', (e) => {
                        e.stopPropagation();
                        this.toggleDropdown(targetId);
                    });
                }
            });
        } catch (error) {
            console.error('Error binding dropdowns:', error);
        }
        
        // Close dropdowns when clicking outside
        document.addEventListener('click', (e) => {
            if (!e.target.closest('[data-dropdown-toggle]') && !e.target.closest('[role="menu"]')) {
                this.closeAllDropdowns();
            }
        });
    }
    
    toggleDropdown(targetId) {
        try {
            const dropdownData = this.dropdowns.get(targetId);
            if (!dropdownData || !dropdownData.dropdown) return;

            const { button, dropdown } = dropdownData;
            const isOpen = !dropdown.classList.contains('hidden');

            // Close all other dropdowns
            this.closeAllDropdowns(targetId);

            if (isOpen) {
                // Close this dropdown
                dropdown.classList.add('hidden');
                button.setAttribute('aria-expanded', 'false');

                const chevron = button.querySelector('[data-dropdown-chevron]');
                if (chevron) {
                    chevron.classList.remove('rotate-180');
                }
            } else {
                // Ensure high z-index
                this.positionDropdown(dropdown, button);

                // Show dropdown
                dropdown.classList.remove('hidden');
                button.setAttribute('aria-expanded', 'true');

                // Rotate chevron if exists
                const chevron = button.querySelector('[data-dropdown-chevron]');
                if (chevron) {
                    chevron.classList.add('rotate-180');
                }
            }
        } catch (error) {
            console.error('Error toggling dropdown:', error);
        }
    }
    
    closeAllDropdowns(except = null) {
        try {
            this.dropdowns.forEach((data, id) => {
                if (id !== except && data.dropdown && data.button) {
                    data.dropdown.classList.add('hidden');
                    data.button.setAttribute('aria-expanded', 'false');
                    
                    const chevron = data.button.querySelector('[data-dropdown-chevron]');
                    if (chevron) {
                        chevron.classList.remove('rotate-180');
                    }
                }
            });
        } catch (error) {
            console.error('Error closing dropdowns:', error);
        }
    }
    
    positionDropdown(dropdown, button) {
        if (!dropdown || !button) return;

        const isCollapsed = this.sidebar && this.sidebar.classList.contains('sidebar-collapsed');
        const isResponsiveCollapsed = window.innerWidth >= 640 && window.innerWidth < 1024;
        const isMoreMenu = dropdown.id === 'more-menu';
        const isUserMenu = dropdown.id === 'user-menu-popover';

        // Always set high z-index
        dropdown.style.zIndex = '99999';

        // User menu popover - position above the user button within sidebar
        if (isUserMenu) {
            dropdown.style.position = 'fixed';

            const buttonRect = button.getBoundingClientRect();

            // Measure dropdown dimensions
            dropdown.style.visibility = 'hidden';
            dropdown.style.display = 'block';
            dropdown.classList.remove('hidden');
            dropdown.offsetHeight;
            const dropdownHeight = dropdown.scrollHeight;

            const viewportPadding = 16;
            const maxHeight = window.innerHeight - (viewportPadding * 2);
            const gap = 8;

            // Position above the button
            let topPosition = buttonRect.top - dropdownHeight - gap;

            if (topPosition < viewportPadding) {
                if (dropdownHeight <= maxHeight) {
                    topPosition = viewportPadding;
                    dropdown.style.maxHeight = '';
                    dropdown.style.overflowY = '';
                } else {
                    topPosition = viewportPadding;
                    dropdown.style.maxHeight = `${maxHeight}px`;
                    dropdown.style.overflowY = 'auto';
                }
            } else {
                dropdown.style.maxHeight = '';
                dropdown.style.overflowY = '';
            }

            // Align left edge with button, use natural width with minimum
            dropdown.style.left = `${buttonRect.left}px`;
            dropdown.style.width = '';
            dropdown.style.minWidth = `${Math.max(buttonRect.width, 240)}px`;
            dropdown.style.top = `${topPosition}px`;
            dropdown.style.bottom = 'auto';
            dropdown.style.right = 'auto';

            // Now make it visible
            dropdown.style.visibility = '';
            return;
        }

        // More menu always uses fixed positioning to the right of button
        if (isMoreMenu) {
            dropdown.classList.remove('absolute', 'bottom-full', 'mb-2', 'left-full', 'ml-2');
            dropdown.style.position = 'fixed';

            const buttonRect = button.getBoundingClientRect();

            // Measure dropdown dimensions
            dropdown.style.visibility = 'hidden';
            dropdown.classList.remove('hidden');
            const dropdownHeight = dropdown.offsetHeight;
            const dropdownWidth = dropdown.offsetWidth;
            dropdown.classList.add('hidden');
            dropdown.style.visibility = '';

            // Position to the right of button
            let leftPosition = buttonRect.right + 8;

            // If it would go off screen right, position to the left of button
            if (leftPosition + dropdownWidth > window.innerWidth - 10) {
                leftPosition = buttonRect.left - dropdownWidth - 8;
            }

            // Vertically align with button, but keep on screen
            let topPosition = buttonRect.top;

            // Make sure it doesn't go off bottom of screen
            if (topPosition + dropdownHeight > window.innerHeight - 10) {
                topPosition = window.innerHeight - dropdownHeight - 10;
            }

            // Make sure it doesn't go off top of screen
            if (topPosition < 10) {
                topPosition = 10;
            }

            dropdown.style.left = `${leftPosition}px`;
            dropdown.style.top = `${topPosition}px`;
            dropdown.style.bottom = 'auto';
            dropdown.style.right = 'auto';
            return;
        }

        if (isCollapsed || isResponsiveCollapsed) {
            // Remove positioning classes that conflict with fixed positioning
            dropdown.classList.remove('absolute', 'bottom-full', 'mb-2', 'left-full', 'ml-2');

            // Use fixed positioning to escape overflow container
            const buttonRect = button.getBoundingClientRect();
            dropdown.style.position = 'fixed';

            // Make dropdown visible temporarily to measure its dimensions
            dropdown.style.visibility = 'hidden';
            dropdown.classList.remove('hidden');
            const dropdownHeight = dropdown.offsetHeight;
            const dropdownWidth = dropdown.offsetWidth;
            dropdown.classList.add('hidden');
            dropdown.style.visibility = '';

            // Position dropdown above button, but ensure it doesn't go past left edge of button
            // Start by centering it over the button
            let leftPosition = buttonRect.left + (buttonRect.width / 2) - (dropdownWidth / 2);

            // If it would extend past the left edge of the button, align it with the button's left edge
            if (leftPosition < buttonRect.left) {
                leftPosition = buttonRect.left;
            }

            dropdown.style.left = `${leftPosition}px`;

            // Position above button with small gap (matching mb-2 = 0.5rem = 8px)
            let topPosition = buttonRect.top - dropdownHeight - 8;

            // If it would go off the top of screen, position it below button instead
            if (topPosition < 10) {
                topPosition = buttonRect.bottom + 8;
                // Make sure it doesn't go off bottom either
                if (topPosition + dropdownHeight > window.innerHeight - 10) {
                    // Just position as high as possible while staying on screen
                    topPosition = Math.max(10, window.innerHeight - dropdownHeight - 10);
                }
            }

            dropdown.style.top = `${topPosition}px`;
            dropdown.style.bottom = 'auto';
            dropdown.style.right = 'auto';
        } else {
            // Restore original classes for expanded sidebar
            dropdown.classList.add('absolute', 'bottom-full', 'mb-2');

            // Clear any inline positioning styles
            dropdown.style.position = '';
            dropdown.style.left = '';
            dropdown.style.top = '';
            dropdown.style.bottom = '';
            dropdown.style.right = '';
        }
    }
    
    bindMobileMenu() {
        try {
            const menuToggle = document.querySelector('[data-mobile-menu-toggle]');
            const mobileSidebar = this.mobileSidebar;

            if (!menuToggle || !mobileSidebar) return;

            const overlay = mobileSidebar.querySelector('[data-mobile-sidebar-overlay]');
            const panel = mobileSidebar.querySelector('[data-mobile-sidebar-panel]');
            const closeButton = mobileSidebar.querySelector('[data-mobile-sidebar-close]');

            if (!panel) return; // Guard against missing elements

        // Open mobile menu
        menuToggle.addEventListener('click', () => {
            mobileSidebar.classList.remove('hidden');
            setTimeout(() => {
                panel.classList.remove('translate-x-full');
            }, 10);
        });
        
        // Close mobile menu
        const closeMobileMenu = () => {
            panel.classList.add('translate-x-full');
            setTimeout(() => {
                mobileSidebar.classList.add('hidden');
            }, 300);
        };
        
        overlay?.addEventListener('click', closeMobileMenu);
        closeButton?.addEventListener('click', closeMobileMenu);
        
            // Close on navigation
            mobileSidebar.querySelectorAll('a').forEach(link => {
                link.addEventListener('click', closeMobileMenu);
            });
        } catch (error) {
            console.error('Error binding mobile menu:', error);
        }
    }

    bindLayoutToggle() {
        try {
            const layoutToggle = document.getElementById('sidebarLayoutToggle');
            if (!layoutToggle || layoutToggle.hasAttribute('data-bound')) return;

            layoutToggle.setAttribute('data-bound', 'true');
            layoutToggle.addEventListener('click', (e) => {
                e.preventDefault();
                const currentLayout = this.getCurrentLayout();
                const nextLayout = this.getNextLayout(currentLayout);

                if (window.switchLayoutPreference) {
                    window.switchLayoutPreference(nextLayout);
                    return;
                }

                window.location.href = window.location.pathname + '?layout=' + nextLayout;
            });

            // Update toggle text
            this.updateLayoutToggleText(layoutToggle);
        } catch (error) {
            console.error('Error binding layout toggle:', error);
        }
    }

    getCurrentLayout() {
        // Get from data attribute (cookie is httpOnly so JS can't read it)
        const toggleElement = document.getElementById('sidebarLayoutToggle');
        if (toggleElement && toggleElement.dataset.currentLayout) {
            return toggleElement.dataset.currentLayout;
        }
        return 'sidebar';
    }

    getNextLayout(currentLayout) {
        const layoutCycle = {
            'sidebar': 'ultra-legacy',
            'ultra-legacy': 'sidebar'
        };
        return layoutCycle[currentLayout] || 'sidebar';
    }

    updateLayoutToggleText(toggleElement) {
        const textElement = toggleElement.querySelector('span') || toggleElement;
        if (textElement) {
            const currentLayout = this.getCurrentLayout();
            const nextLayout = this.getNextLayout(currentLayout);
            // Use translated text from data attributes
            const newText = nextLayout === 'sidebar'
                ? toggleElement.dataset.textNew
                : toggleElement.dataset.textClassic;
            if (newText) {
                textElement.textContent = newText;
            }
        }
    }

    bindKeyboard() {
        try {
            document.addEventListener('keydown', (e) => {
                // Alt + S: Toggle sidebar
                if (e.altKey && e.key === 's') {
                    e.preventDefault();
                    this.toggleSidebar();
                }
                
                // Escape: Close dropdowns, moderation menu, and mobile menu
                if (e.key === 'Escape') {
                    this.closeAllDropdowns();

                    // Close moderation menu if open
                    if (this.moderationMenuOpen) {
                        const moderationMenuExpanded = document.querySelector('[data-moderation-menu-expanded]');
                        const moderationToggle = document.querySelector('[data-moderation-toggle]');
                        const moderationChevron = document.querySelector('[data-moderation-chevron]');

                        if (moderationMenuExpanded) {
                            this.collapseModerationMenu(moderationMenuExpanded, moderationToggle, moderationChevron);
                            this.moderationMenuOpen = false;
                            this.saveState('moderation_menu_state', 'collapsed');
                        }
                    }

                    // Close mobile menu if open
                    const panel = this.mobileSidebar?.querySelector('[data-mobile-sidebar-panel]');
                    if (panel && !panel.classList.contains('translate-x-full')) {
                        panel.classList.add('translate-x-full');
                        setTimeout(() => {
                            if (this.mobileSidebar) {
                                this.mobileSidebar.classList.add('hidden');
                            }
                        }, 300);
                    }
                }
            });
        } catch (error) {
            console.error('Error binding keyboard shortcuts:', error);
        }
    }
    
    handleResize() {
        let resizeTimer;
        
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                // Close dropdowns on resize
                this.closeAllDropdowns();
            }, 250);
        });
    }
    
}

// Initialize on DOM ready
function initializeNavigation() {
    try {
        const nav = new Navigation();
    } catch (error) {
        console.error('Failed to initialize navigation:', error);
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeNavigation);
} else {
    initializeNavigation();
}

// Export for use in other modules if needed
window.Navigation = Navigation;
